import classNames from "classnames";
import LinkWrapper from "next/link";
import { TextLinkProps, TextLink } from "@/client/foundations/Typography/TextLink/TextLink";
import { HTMLAttributeAnchorTarget } from "react";
// import { Box } from "@fastlooptech/layout-components";
import { useParams, usePathname } from "next/navigation";
import { Box } from "@/layouts/LayoutComponents";

interface LinkProps extends Omit<TextLinkProps, "children" | "size"> {
  /**
   * Define the id of the Link.
   * @type string
   * @optional
   */
  id?: string;
  /**
   * Define the custom className of the Link wrapper.
   * @type string
   * @optional
   */
  classNameWrapper?: string;
  /**
   * Define the custom className of the Link content.
   * @type string
   * @optional
   */
  className?: string;
  /**
   * Define the content of the Link.
   * @type string
   * @required
   */
  label: string;
  /**
   * Define the href of the Link.
   * @type string
   * @optional
   */
  href?: string;
  /**
   * Define the target of the Link.
   * @type string
   * @optional
   */
  locale?: string;
  target?: HTMLAttributeAnchorTarget;
  /**
   * Define the action of the Link.
   * @type () => void
   * @optional
   */
  action?: () => void;
}

export function Link({ id, classNameWrapper, className, label, href="", target, locale, action }: LinkProps) {
  const params = useParams<{ locale: string; }>();
  const pathname = usePathname();

  let targetHref = href;
  if (href && pathname === ('/' + params.locale)) {
    targetHref = '/' + params.locale + (href.indexOf('/') !== 0 ? ('/' + href) : href);
  }

  return href ? (
    <LinkWrapper
      id={id}
      href={targetHref}
      aria-label={label}
      target={target}
      className={classNames(
        "px-0.5 py-1 w-fit box-border border-b-2 border-transparent",
        "hover:bg-interactive-yellow hover:border-y-interactive-black",
        "focus:border-y-interactive-black",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-interactive-black focus-visible:outline-offset-0 focus-visible:border-transparent",
        classNameWrapper,
      )}
      locale={locale}
    >
      <TextLink size="xs" bold className={className}>
        {label}
      </TextLink>
    </LinkWrapper>
  ) : (
    <Box
      id={id}
      onClick={action}
      className={classNames(
        "px-0.5 py-1 w-fit box-border border-b-2 border-transparent",
        "hover:bg-interactive-yellow hover:border-y-interactive-black hover:cursor-pointer",
        "focus:border-y-interactive-black",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-interactive-black focus-visible:outline-offset-0 focus-visible:border-transparent",
        classNameWrapper,
      )}
    >
      <TextLink size="xs" bold className={className}>
        {label}
      </TextLink>
    </Box>
  );
}
